import {ZodType} from 'zod';

import {isNilOrEmpty} from 'ramda-adjunct';

import {AccountingMatrixDatagridCode, AccountingMatrixDatagridRecord} from '@dms/api/finance';
import i18n from '@dms/i18n';

/**
 * Generic detail-form config for accounting matrix datagrids. The list itself is a BE-defined
 * eag-grid (DmsDataGrid, gridCode `accounting-{dgCode}` — see getAccountingMatrixDatagridGridCode
 * below); this config only drives the create/edit dialog form (fields + zod schema), extracted
 * from the M101 Cash implementation (T20-137877) once a second datagrid (T20-137879) needed the
 * same form/CRUD wiring. See T20-137873 for the original foundation scope.
 *
 * Field names/types/nesting are taken from the dev swagger (`accounting/v1/openapi.json`,
 * checked 2026-09-24), not from the epic's prose field list, which turned out to not match the
 * real request bodies (e.g. codelist-backed fields are `{code, translation}` objects, not plain
 * strings; the shared "Organizace" field is `billingInformation: {id, cin, name}`, not a flat
 * `billingInformationId`).
 */
export type AccountingMatrixDatagridFieldType = 'text' | 'apiDate' | 'number' | 'choice' | 'switch';

/** Codelist categories the generic form knows how to resolve options for (see T20-137873). */
export type AccountingMatrixDatagridCodelistCategory =
  | 'ACCapplicationModule'
  | 'ACCoperationArea'
  | 'ACCitemGroups';

export interface AccountingMatrixDatagridFieldConfig {
  name: string;
  label: string;
  type: AccountingMatrixDatagridFieldType;
  isRequired?: boolean;
  /** Required when `type` is `'choice'` — selects which codelist to load options from. */
  codelistCategory?: AccountingMatrixDatagridCodelistCategory;
  /** When set, the field is only rendered while it returns true for the current form values
   *  (e.g. M202/M205's `authorizationProfileCode`/`orderVariantSuffix`, shown only for
   *  `application === 'workshop'`, see T20-137883 / T20-137885). */
  showWhen?: (values: Record<string, unknown>) => boolean;
  /** Reads this field's flat form value out of a fetched record. Defaults to `record[name]`;
   *  override for fields the API nests (see `accountingMatrixDatagridCodeObjectField` /
   *  `accountingMatrixDatagridBillingInformationField` below). */
  fromRecord?: (record: AccountingMatrixDatagridRecord) => unknown;
  /** Shapes this field's flat form value into the request body. Defaults to the value as-is. */
  toBody?: (value: unknown) => unknown;
}

export interface AccountingMatrixDatagridConfig {
  dgCode: AccountingMatrixDatagridCode;
  fields: AccountingMatrixDatagridFieldConfig[];
  schema: ZodType<Record<string, unknown>>;
}

/**
 * eag-grid `gridCode` for a datagrid's BE-defined grid (list/columns/filters/actions all come
 * from BE — see T20-134805 demo page); the FE only ever references it by this string.
 *
 * Every M-coded matrix follows `accounting-{dgCode}` (e.g. `accounting-m13-vat`). Operation
 * Items doesn't have an M-code and BE registered it under a different name — confirmed against
 * the demo page as `accounting-operation-item-codes`, not the `accounting-operation-items` the
 * naming pattern would otherwise imply.
 */
export const getAccountingMatrixDatagridGridCode = (dgCode: AccountingMatrixDatagridCode) => {
  if (dgCode === 'operation-items') {
    return 'accounting-operation-item-codes';
  }
  return `accounting-${dgCode}`;
};

/** Narrows an unknown value to an object exposing `key` as a string, without an `as` assertion. */
const hasStringProperty = <K extends string>(
  value: unknown,
  key: K
): value is Record<K, string | undefined> =>
  typeof value === 'object' && value !== null && key in value;

/**
 * `fromRecord`/`toBody` pair for a codelist-backed field the API nests as `{code, translation}`
 * (e.g. `application`, `operationArea`, `itemGroup`, `paymentMethod`) — the form only ever holds
 * the flat `code` string, matching what the `choice` FormField/codelist options work with.
 */
export const accountingMatrixDatagridCodeObjectField = (fieldName: string) => ({
  fromRecord: (record: AccountingMatrixDatagridRecord) => {
    const value = record[fieldName];
    return hasStringProperty(value, 'code') ? (value.code ?? '') : '';
  },
  toBody: (value: unknown) => (isNilOrEmpty(value) ? null : {code: value}),
});

/**
 * `fromRecord`/`toBody` pair for the shared `billingInformation: {id, cin, name}` field — the
 * form only ever holds the flat `id`. `cin`/`name` are read-only nested attributes populated by
 * BE, not sent back; its real source (codelist vs. DMS entity lookup) is still open, see
 * T20-137871 point 3 / follow-up.
 */
const accountingMatrixDatagridBillingInformationField = () => ({
  fromRecord: (record: AccountingMatrixDatagridRecord) => {
    const value = record.billingInformation;
    return hasStringProperty(value, 'id') ? (value.id ?? '') : '';
  },
  toBody: (value: unknown) => (isNilOrEmpty(value) ? null : {id: value}),
});

/**
 * Fields shared by every datagrid (see epic T20-134805). IČO (billing company CIN) is
 * intentionally left out — it's a read-only nested attribute of `billingInformation`, not sent
 * in PATCH/POST.
 */
export const getAccountingMatrixDatagridSharedFields =
  (): AccountingMatrixDatagridFieldConfig[] => [
    {
      name: 'billingInformation',
      label: i18n.t('entity.customer.labels.billingCompany'),
      type: 'text',
      isRequired: true,
      ...accountingMatrixDatagridBillingInformationField(),
    },
    {
      name: 'validFrom',
      label: i18n.t('page.accountingSettings.labels.validFrom'),
      type: 'apiDate',
      isRequired: true,
    },
    {
      name: 'validTo',
      label: i18n.t('page.accountingSettings.labels.validTo'),
      type: 'apiDate',
      isRequired: true,
    },
  ];
